import type { RootAction } from '@app/store/rootAction.js';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Alert, Button, ButtonToolbar, CloseButton } from 'react-bootstrap';
import type { ResolvedToast, ToastAction } from '../model/actions.js';
import classes from './Toast.module.css';

interface Props extends Pick<ResolvedToast, 'id' | 'style' | 'noClose'> {
  actions: (Omit<ToastAction, 'nameKey'> & { name: string })[];
  onAction: (id: string, action?: RootAction | RootAction[]) => void;
  onClose: (id: string) => void;
  onTimeoutStop: (id: string) => void;
  onTimeoutRestart: (id: string) => void;
  message: ReactNode;
  timeout?: number;
  timeoutSince?: number;
}

export function Toast({
  message,
  actions,
  onAction,
  onClose,
  id,
  style,
  onTimeoutStop,
  onTimeoutRestart,
  noClose,
  timeout,
  timeoutSince,
}: Props): ReactElement {
  const [elapsed, setElapsed] = useState(0);

  const [stopped, setStopped] = useState(false);

  const handlePointerEnter = useCallback(() => {
    if (timeout === undefined) {
      return;
    }

    setStopped(true);
    setElapsed(0);
    onTimeoutStop(id);
  }, [onTimeoutStop, id, timeout]);

  const handlePointerLeave = useCallback(() => {
    if (timeout === undefined) {
      return;
    }

    setStopped(false);
    onTimeoutRestart(id);
  }, [onTimeoutRestart, id, timeout]);

  const handleAlertDismiss = useCallback(() => {
    onClose(id);
  }, [onClose, id]);

  const defaultAction = actions.find(({ name }) => !name);

  const clickHandler =
    defaultAction && (() => onAction(id, defaultAction.action));

  const buttonActions = actions.filter(({ name }) => name);

  useEffect(() => {
    if (timeout === undefined || timeoutSince === undefined || stopped) {
      return;
    }

    const ref = window.setInterval(() => {
      setElapsed(Date.now() - timeoutSince);
    }, 25);

    return () => window.clearInterval(ref);
  }, [timeoutSince, stopped, timeout]);

  return (
    <Alert
      className={classes.toast}
      variant={style ?? 'primary'}
      onClick={clickHandler}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      {noClose ? undefined : (
        <CloseButton className="float-end" onClick={handleAlertDismiss} />
      )}

      <span>{message}</span>

      {/* `gap` rather than a margin per button: the toolbar wraps, and a margin
          would indent whichever button starts the second row and leave the rows
          touching. */}
      {buttonActions.length > 0 && (
        <ButtonToolbar className="mt-2 gap-2">
          {buttonActions.map(
            ({ name, action, href, variant: buttonStyle }, i) => (
              <Button
                size="sm"
                key={i}
                variant={buttonStyle}
                // A link still dismisses: the toast has been acted on, and one
                // that survived an `intent://` hand-off would be waiting on return
                // to say something the user has already dealt with.
                href={href}
                onClick={() => onAction(id, action)}
              >
                {name}
              </Button>
            ),
          )}
        </ButtonToolbar>
      )}

      {timeout !== undefined && (
        <div
          className="bg-body"
          style={{ position: 'relative', top: '0.5rem' }}
        >
          <div
            className="bg-primary"
            style={{
              width: `${((timeout - elapsed) / timeout) * 100}%`,
              height: '2px',
            }}
          />
        </div>
      )}
    </Alert>
  );
}
